import type { CSSProperties } from 'react';

interface LoadingSpinnerProps {
  size?: number; // in pixels, default 48
  className?: string;
}

/** The default box; its cell / gap live in app.css (.loading-spinner), so a context can restyle it (.viewer-loading). */
const DEFAULT_SIZE = 48;

/**
 * Whole-pixel cell and gap for a `size` px box: 3 cells + 2 gaps fit inside it, the gap ~ size / 12 (>= 1 px), the
 * cell >= 2 px (12 px -> 3 px cells), so the grid stays crisp at button sizes.
 */
function dotMetrics(size: number): { cell: number; gap: number } {
  const gap = Math.max(1, Math.round(size / 12));
  const cell = Math.max(2, Math.floor((size - 2 * gap) / 3));
  return { cell, gap };
}

/**
 * The loading indicator: a 3 x 3 dot matrix of square cells in the current text colour. A lit cell walks the outer
 * ring with a short fading trail around an always-lit centre (app.css, pure CSS; reduced motion: a still
 * pattern that breathes). Decorative -- the copy next to it ("Loading...", the button label) carries the meaning.
 */
export function LoadingSpinner({ size = DEFAULT_SIZE, className = '' }: LoadingSpinnerProps) {
  let style: CSSProperties | undefined;
  if (size !== DEFAULT_SIZE) {
    const { cell, gap } = dotMetrics(size);
    style = {
      width: `${size}px`,
      height: `${size}px`,
      '--pmt-dots-cell': `${cell}px`,
      '--pmt-dots-gap': `${gap}px`,
    } as CSSProperties;
  }

  return (
    <div className={`loading-spinner ${className}`.trim()} style={style} aria-hidden="true">
      {Array.from({ length: 9 }, (_, i) => (
        <span key={i} className="loading-spinner__cell" />
      ))}
    </div>
  );
}
